import { Notification03Icon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { enhancedNotify } from '@pique/core/components/shared/notification-utils';
import type { FetchService } from '@pique/core/modules/cloud/services/fetch';

import {
  getDisplayTextFromDate,
  REMINDER_MESSAGES,
} from './reminder-constants';

const TODO_FOLLOWUP_WORKFLOW_ID = 'todo-followup-reminder';

const TODO_FOLLOWUP_DEFAULT_CHANNELS = {
  in_app: true,
  push: true,
  email: false,
  chat: false,
  sms: false,
};

function getReminderToastBase() {
  return {
    icon: <HugeiconsIcon icon={Notification03Icon} size={16} />,
    rootAttrs: {
      style: {
        backgroundColor: 'var(--affine-background-primary-color)',
        color: 'var(--affine-text-primary-color)',
        borderColor: 'var(--affine-border-color)',
        boxShadow: 'var(--affine-menu-shadow)',
        borderRadius: '16px',
        border: '1px solid var(--affine-border-color)',
      },
    },
  };
}

async function ensureTodoFollowupNovuPreference(novu: unknown): Promise<void> {
  const client = novu as {
    preferences?: {
      update?: (params: {
        workflowId: string;
        channels: Record<string, boolean>;
      }) => Promise<unknown>;
    };
  };
  await client.preferences?.update?.({
    workflowId: TODO_FOLLOWUP_WORKFLOW_ID,
    channels: { ...TODO_FOLLOWUP_DEFAULT_CHANNELS },
  });
}

export async function notifyFollowupReminderSet(params: {
  scheduledAt: string;
}): Promise<void> {
  const scheduledDate = new Date(params.scheduledAt);
  const dateText = getDisplayTextFromDate(scheduledDate);
  void enhancedNotify
    .success(
      {
        ...getReminderToastBase(),
        title: REMINDER_MESSAGES.CONFIRMATION_TITLE,
        message: REMINDER_MESSAGES.CONFIRMATION_MESSAGE_AT(dateText),
      },
      {
        duration: 2500,
        position: 'top-center',
        style: { borderRadius: '10px' },
      }
    )
    .catch(() => {});
}

export async function postFollowupReminder(
  fetchService: FetchService,
  body: {
    itemId: string;
    title: string;
    message?: string;
    scheduledAt: string;
  }
): Promise<string> {
  const { itemId, title, message, scheduledAt } = body;
  if (!itemId || !title || !scheduledAt) throw new Error('Missing required fields');

  const res = await fetchService.fetch('/api/calendar/followup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      itemId,
      title,
      message: message ?? REMINDER_MESSAGES.DEFAULT,
      scheduledAt,
    }),
  });
  const data = (await res.json()) as { transactionId: string };
  return data.transactionId;
}

export async function cancelFollowupReminder(
  fetchService: FetchService,
  transactionId: string,
): Promise<void> {
  await fetchService.fetch(
    `/api/calendar/followup/${encodeURIComponent(transactionId)}`,
    { method: 'DELETE' },
  );
}

export async function scheduleFollowupReminder(params: {
  fetchService: FetchService;
  novu: unknown;
  itemId: string;
  title: string;
  scheduledAt: string;
  message?: string;
}): Promise<string> {
  await ensureTodoFollowupNovuPreference(params.novu);
  return postFollowupReminder(params.fetchService, {
    itemId: params.itemId,
    title: params.title,
    message: params.message,
    scheduledAt: params.scheduledAt,
  });
}
